import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import SavedSearchSelect from "./SavedSearchSelect";
import SaveSearchDialog from "./SaveSearchDialog";
import DeleteSavedSearchDialog from "./DeleteSavedSearchDialog";
import type { SearchFilters } from "../FilterBar/filterChipDefs";
import { filtersEqual, toSearchFilters, toGetInteractionsRequest } from "./savedSearchBridge";
import {
  useGetSavedSearch,
  useGetSavedSearches,
  useCreateSavedSearch,
  useUpdateSavedSearch,
  useDeleteSavedSearch,
} from "../../../hooks/useSavedSearch";
import { useAlert } from "../../../context/AlertContext";
import ResourceInUseError from "../../../errors/ResourceInUseError";

export interface SavedSearchBarProps {
  readonly customerId: string;
  readonly userId: string;
  readonly filters: SearchFilters;
  readonly onFiltersChange: (next: SearchFilters) => void;
}

export default function SavedSearchBar({
  customerId,
  userId,
  filters,
  onFiltersChange,
}: SavedSearchBarProps) {
  const { t } = useTranslation(["search"]);
  const showAlert = useAlert();

  const [loadedSavedSearchId, setLoadedSavedSearchId] = useState<string | null>(null);
  const [appliedId, setAppliedId] = useState<string | null>(null);

  const { data: loadedRecord } = useGetSavedSearch(customerId, loadedSavedSearchId ?? undefined);
  const { data: allSavedSearches } = useGetSavedSearches(customerId, userId);
  const { mutateAsync: createMut } = useCreateSavedSearch();
  const { mutateAsync: updateMut } = useUpdateSavedSearch();
  const { mutateAsync: deleteMut } = useDeleteSavedSearch();

  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInUseError, setDeleteInUseError] = useState(false);

  const loadedFilters = useMemo<SearchFilters | null>(() => {
    if (!loadedRecord) return null;
    return toSearchFilters(loadedRecord.search);
  }, [loadedRecord]);

  const hasAnyFilter = useMemo(() => Object.values(filters).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== "" && v !== null;
  }), [filters]);

  const isModified = loadedSavedSearchId != null && loadedFilters != null
    && !filtersEqual(filters, loadedFilters);

  // Apply filters once the loaded record resolves.
  useEffect(() => {
    if (loadedSavedSearchId && loadedRecord && appliedId !== loadedSavedSearchId) {
      const converted = toSearchFilters(loadedRecord.search);
      onFiltersChange(converted);
      setAppliedId(loadedSavedSearchId);
    }
  }, [loadedSavedSearchId, loadedRecord, appliedId, onFiltersChange]);

  // Drop the saved-search association when the user clears all filters after a
  // non-empty search was loaded. Without this, the dropdown stays stuck on the
  // loaded name, Save would overwrite the record with an empty filter set, and
  // users lose the data they just "cleared out of".
  useEffect(() => {
    if (!loadedSavedSearchId || !loadedFilters) return;
    if (appliedId !== loadedSavedSearchId) return;
    const loadedHasAnyFilter = Object.values(loadedFilters).some((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== "" && v !== null;
    });
    if (loadedHasAnyFilter && !hasAnyFilter) {
      setLoadedSavedSearchId(null);
      setAppliedId(null);
    }
  }, [loadedSavedSearchId, loadedFilters, appliedId, hasAnyFilter]);

  const handleLoad = useCallback(
    (id: string) => {
      setLoadedSavedSearchId(id);
    },
    []
  );

  const handleClear = useCallback(() => {
    setLoadedSavedSearchId(null);
    setAppliedId(null);
    onFiltersChange({});
  }, [onFiltersChange]);

  const handleSaveAs = useCallback(async (name: string) => {
    try {
      const created = await createMut({
        customerId,
        name,
        searchRequest: toGetInteractionsRequest(filters),
      });
      setSaveAsOpen(false);
      showAlert(t("search:savedSearch.v2.createSuccess", { name }), "success");
      if (created?.savedSearchId) {
        setLoadedSavedSearchId(created.savedSearchId);
        setAppliedId(created.savedSearchId);
      }
    } catch {
      showAlert(t("search:savedSearch.v2.genericError"), "error");
    }
  }, [createMut, customerId, filters, showAlert, t]);

  const handleSave = useCallback(async () => {
    if (!loadedSavedSearchId || !loadedRecord) return;
    try {
      await updateMut({
        customerId,
        savedSearchId: loadedSavedSearchId,
        name: loadedRecord.name,
        searchRequest: toGetInteractionsRequest(filters),
      });
      showAlert(t("search:savedSearch.v2.saveSuccess", { name: loadedRecord.name }), "success");
    } catch {
      showAlert(t("search:savedSearch.v2.genericError"), "error");
    }
  }, [updateMut, customerId, loadedSavedSearchId, loadedRecord, filters, showAlert, t]);

  const handleRevert = useCallback(() => {
    if (loadedFilters) onFiltersChange(loadedFilters);
  }, [loadedFilters, onFiltersChange]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!loadedSavedSearchId || !loadedRecord) return;
    try {
      await deleteMut({ customerId, savedSearchId: loadedSavedSearchId });
      showAlert(
        t("search:savedSearch.v2.deleteSuccess", { name: loadedRecord.name }),
        "success"
      );
      setDeleteOpen(false);
      setDeleteInUseError(false);
      setLoadedSavedSearchId(null);
      setAppliedId(null);
    } catch (e) {
      if (e instanceof ResourceInUseError) {
        setDeleteInUseError(true);
      } else {
        showAlert(t("search:savedSearch.v2.genericError"), "error");
      }
    }
  }, [deleteMut, customerId, loadedSavedSearchId, loadedRecord, showAlert, t]);

  const actionButtonSx = {
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    minWidth: "auto",
    px: "10px",
    py: "4px",
    color: "var(--text-secondary)",
    "&.Mui-disabled": {
      color: "var(--text-tertiary)",
      opacity: 0.4,
    },
  };

  return (
    <>
      <Box
        component="section"
        aria-label={t("search:savedSearch.v2.label")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: "14px",
          py: "6px",
          borderBottom: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <Box
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--text-tertiary)",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          {t("search:savedSearch.v2.label")}
        </Box>
        <SavedSearchSelect
          customerId={customerId}
          userId={userId}
          selectedId={loadedSavedSearchId}
          onLoad={handleLoad}
          onClear={handleClear}
        />
        <Button size="small" sx={actionButtonSx} onClick={handleSave}
          disabled={!(loadedSavedSearchId != null && isModified)}>
          {t("search:savedSearch.v2.save")}
        </Button>
        <Button size="small" sx={actionButtonSx} onClick={() => setSaveAsOpen(true)}
          disabled={!hasAnyFilter}>
          {t("search:savedSearch.v2.saveAs")}
        </Button>
        <Button size="small" sx={actionButtonSx} onClick={handleRevert} disabled={!isModified}>
          {t("search:savedSearch.v2.revert")}
        </Button>
        <Button size="small" sx={actionButtonSx}
          onClick={() => { setDeleteInUseError(false); setDeleteOpen(true); }}
          disabled={loadedSavedSearchId == null}>
          {t("search:savedSearch.v2.delete")}
        </Button>
        <Box sx={{ flex: 1 }} />
        {isModified && (
          <Box
            role="status"
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              color: "warning.main",
            }}
          >
            {t("search:savedSearch.v2.modified")}
          </Box>
        )}
      </Box>
      <SaveSearchDialog
        open={saveAsOpen}
        existingNames={(allSavedSearches ?? []).map((s) => s.name)}
        onSubmit={handleSaveAs}
        onClose={() => setSaveAsOpen(false)}
      />
      <DeleteSavedSearchDialog
        open={deleteOpen}
        name={loadedRecord?.name ?? ""}
        inUseError={deleteInUseError}
        onConfirm={handleDeleteConfirm}
        onClose={() => { setDeleteOpen(false); setDeleteInUseError(false); }}
      />
    </>
  );
}
